/**
 * 滤镜（M3-13 / DESIGN.md §5.4）。
 *
 * 高斯模糊直接调用 M0-10 尖刺留下的 wasm 定点实现（`gaussian_blur_rgba8`，
 * 可分离卷积、逐位确定性）。多线程分带管线（`blur_pass_horizontal/vertical`）
 * 已在 wasm 里就绪，本任务先接单线程调用打通滤镜框架；分带调度是独立的
 * 性能任务，不在此文件重复造轮子。
 *
 * USM 锐化 = 原图 + amount·(原图 − 模糊图)，按 threshold 门控——
 * 差值小于阈值的像素不锐化，避免把平坦区域的噪声放大。
 */
import { gaussian_blur_rgba8 } from '../../wasm/core/photosmith_core';
import { ensureKernel } from '../wasm-kernel';

export interface FilterOptions {
  readonly signal?: AbortSignal;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

/** 高斯模糊。原地修改 pixels 的拷贝并返回；不改动传入的数组。 */
export async function gaussianBlur(
  pixels: Uint8Array,
  width: number,
  height: number,
  radius: number,
  options: FilterOptions = {},
): Promise<Uint8Array> {
  throwIfAborted(options.signal);
  await ensureKernel();
  throwIfAborted(options.signal);
  const out = pixels.slice();
  gaussian_blur_rgba8(out, width, height, Math.max(0, Math.round(radius)));
  return out;
}

export interface UnsharpMaskParams {
  /** 锐化强度，0–5（Photoshop 的 1%–500%/100）。 */
  readonly amount: number;
  /** 模糊半径（像素）。 */
  readonly radius: number;
  /** 门控阈值，0–255 的原始差值。 */
  readonly threshold: number;
}

/** USM 锐化：edge = orig − blur，|edge| > threshold 的像素才叠加。 */
export async function unsharpMask(
  pixels: Uint8Array,
  width: number,
  height: number,
  params: UnsharpMaskParams,
  options: FilterOptions = {},
): Promise<Uint8Array> {
  throwIfAborted(options.signal);
  const blurred = await gaussianBlur(pixels, width, height, params.radius, options);
  throwIfAborted(options.signal);

  const out = new Uint8Array(pixels.length);
  for (let i = 0; i < pixels.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const orig = pixels[i + c]!;
      const blur = blurred[i + c]!;
      const diff = orig - blur;
      const value = Math.abs(diff) > params.threshold ? orig + diff * params.amount : orig;
      out[i + c] = Math.min(255, Math.max(0, Math.round(value)));
    }
    out[i + 3] = pixels[i + 3]!; // alpha 不参与锐化
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* 对比 Photoshop 补全第一档缺口 阶段 3：5 个新滤镜                        */
/*                                                                      */
/* 全部纯 TS，不调 wasm——这批滤镜没有现成的 Rust 核，规模也小到不值得为它们  */
/* 单独开一个 wasm 入口（跟高斯模糊/USM 不同，那两个复用的是 M0-10 已有的   */
/* 定点实现）。都不接入智能滤镜链（`smart-filter-render.ts` 只认高斯模糊/   */
/* USM 这两种 kind，未知 kind 走无操作兜底），跟现有非这两种滤镜的待遇一致，  */
/* 已在完成报告披露。                                                      */
/* ------------------------------------------------------------------ */

/** 双线性取样，坐标越界钳到画布边缘（不是补零——避免边缘变暗/变透明）。 */
function sampleClamped(pixels: Uint8Array, width: number, height: number, x: number, y: number, channel: number): number {
  const cx = Math.min(width - 1, Math.max(0, Math.round(x)));
  const cy = Math.min(height - 1, Math.max(0, Math.round(y)));
  return pixels[(cy * width + cx) * 4 + channel]!;
}

export interface MotionBlurParams {
  /** 方向，度（−180–180）。 */
  readonly angle: number;
  /** 位移距离，像素（0–200）。 */
  readonly distance: number;
}

/**
 * 动感模糊：沿 `angle` 方向、跨度 `distance` 像素的定向平均——不是可分离
 * 高斯，是最直接的"沿一条线打点求平均"（PS 的动感模糊视觉上就是这个效果）。
 * 复杂度 O(宽×高×distance)，`distance` 上限 200 时够快；没有为大距离做
 * 额外优化（比如分离成常数时间的滑动窗口），这批新滤镜的规模不值得。
 */
export async function motionBlur(
  pixels: Uint8Array,
  width: number,
  height: number,
  params: MotionBlurParams,
  options: FilterOptions = {},
): Promise<Uint8Array> {
  throwIfAborted(options.signal);
  const steps = Math.max(1, Math.round(Math.min(200, Math.max(0, params.distance))));
  const rad = (params.angle * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const out = new Uint8Array(pixels.length);
  for (let y = 0; y < height; y++) {
    if (y % 64 === 0) throwIfAborted(options.signal);
    for (let x = 0; x < width; x++) {
      const o = (y * width + x) * 4;
      for (let c = 0; c < 4; c++) {
        let sum = 0;
        for (let s = 0; s < steps; s++) {
          const t = s - (steps - 1) / 2;
          sum += sampleClamped(pixels, width, height, x + dx * t, y + dy * t, c);
        }
        out[o + c] = Math.round(sum / steps);
      }
    }
  }
  return out;
}

export interface SurfaceBlurParams {
  /** 采样半径，像素（0–100）。 */
  readonly radius: number;
  /** 保边阈值，0–255——邻域像素跟中心差超过阈值就不参与平均。 */
  readonly threshold: number;
}

/**
 * 表面模糊：保边模糊的简化近似——方窗内只平均"跟中心足够接近"的邻居
 * （按三通道差的最大值判定），边缘因为两侧差异大于阈值而几乎不被模糊，
 * 平坦区域正常被模糊。不是真正的双边滤波（没有距离/相似度的连续权重，
 * 是硬阈值二值门控），但视觉方向一致：保边、抹平坦。
 */
export async function surfaceBlur(
  pixels: Uint8Array,
  width: number,
  height: number,
  params: SurfaceBlurParams,
  options: FilterOptions = {},
): Promise<Uint8Array> {
  throwIfAborted(options.signal);
  const radius = Math.max(0, Math.min(100, Math.round(params.radius)));
  if (radius === 0) return pixels.slice();
  const threshold = Math.max(0, Math.min(255, params.threshold));
  const out = new Uint8Array(pixels.length);
  for (let y = 0; y < height; y++) {
    if (y % 32 === 0) throwIfAborted(options.signal);
    for (let x = 0; x < width; x++) {
      const centerOffset = (y * width + x) * 4;
      const cr = pixels[centerOffset]!;
      const cg = pixels[centerOffset + 1]!;
      const cb = pixels[centerOffset + 2]!;
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let count = 0;
      for (let ny = Math.max(0, y - radius); ny <= Math.min(height - 1, y + radius); ny++) {
        for (let nx = Math.max(0, x - radius); nx <= Math.min(width - 1, x + radius); nx++) {
          const no = (ny * width + nx) * 4;
          const nr = pixels[no]!;
          const ng = pixels[no + 1]!;
          const nb = pixels[no + 2]!;
          const diff = Math.max(Math.abs(nr - cr), Math.abs(ng - cg), Math.abs(nb - cb));
          if (diff > threshold) continue;
          sumR += nr;
          sumG += ng;
          sumB += nb;
          count++;
        }
      }
      const o = centerOffset;
      out[o] = count > 0 ? Math.round(sumR / count) : cr;
      out[o + 1] = count > 0 ? Math.round(sumG / count) : cg;
      out[o + 2] = count > 0 ? Math.round(sumB / count) : cb;
      out[o + 3] = pixels[o + 3]!;
    }
  }
  return out;
}

export interface AddNoiseParams {
  /** 数量，0–100（映射到 ±100 灰度级的均匀噪声幅度）。 */
  readonly amount: number;
  /** 单色：三通道加同一份噪声（不改变色相，只改变明度）。 */
  readonly monochrome: boolean;
}

/**
 * 添加杂色：每像素每通道叠加 [−amount%, +amount%]（换算到 0–255 尺度）的
 * 均匀分布噪声。`random` 可注入（测试用确定性生成器），默认 `Math.random`。
 */
export async function addNoise(
  pixels: Uint8Array,
  width: number,
  height: number,
  params: AddNoiseParams,
  options: FilterOptions = {},
  random: () => number = Math.random,
): Promise<Uint8Array> {
  throwIfAborted(options.signal);
  const amplitude = (Math.max(0, Math.min(100, params.amount)) / 100) * 255;
  const out = new Uint8Array(pixels.length);
  const count = width * height;
  for (let i = 0; i < count; i++) {
    if (i % 8192 === 0) throwIfAborted(options.signal);
    const o = i * 4;
    if (params.monochrome) {
      const n = (random() * 2 - 1) * amplitude;
      for (let c = 0; c < 3; c++) out[o + c] = Math.round(Math.min(255, Math.max(0, pixels[o + c]! + n)));
    } else {
      for (let c = 0; c < 3; c++) {
        const n = (random() * 2 - 1) * amplitude;
        out[o + c] = Math.round(Math.min(255, Math.max(0, pixels[o + c]! + n)));
      }
    }
    out[o + 3] = pixels[o + 3]!;
  }
  return out;
}

export interface EmbossParams {
  /** 光源方向，度（−180–180）。 */
  readonly angle: number;
  /** 采样距离，像素（1–10），对应 PS 的"高度"。 */
  readonly height: number;
  /** 强度，0–500（百分之几）。 */
  readonly amount: number;
}

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * 浮雕：灰度化，用当前像素跟"光源方向上距离 height 处"的像素的灰度差
 * （边缘强度）叠加到中性灰 128 上，`amount` 控制叠加强度。跟斜面浮雕
 * 图层样式同一光照直觉，这里重新实现成一个独立的、作用于整图像素的滤镜
 * （不依赖图层样式的合成管线）。
 */
export async function emboss(
  pixels: Uint8Array,
  width: number,
  height: number,
  params: EmbossParams,
  options: FilterOptions = {},
): Promise<Uint8Array> {
  throwIfAborted(options.signal);
  const rad = (params.angle * Math.PI) / 180;
  const sampleDist = Math.max(1, Math.min(10, params.height));
  const dx = Math.cos(rad) * sampleDist;
  const dy = Math.sin(rad) * sampleDist;
  const strength = Math.max(0, Math.min(500, params.amount)) / 100;
  const out = new Uint8Array(pixels.length);
  for (let y = 0; y < height; y++) {
    if (y % 64 === 0) throwIfAborted(options.signal);
    for (let x = 0; x < width; x++) {
      const o = (y * width + x) * 4;
      const gray = luminance(pixels[o]!, pixels[o + 1]!, pixels[o + 2]!);
      const shiftedR = sampleClamped(pixels, width, height, x + dx, y + dy, 0);
      const shiftedG = sampleClamped(pixels, width, height, x + dx, y + dy, 1);
      const shiftedB = sampleClamped(pixels, width, height, x + dx, y + dy, 2);
      const shiftedGray = luminance(shiftedR, shiftedG, shiftedB);
      const value = Math.round(Math.min(255, Math.max(0, 128 + (gray - shiftedGray) * strength)));
      out[o] = value;
      out[o + 1] = value;
      out[o + 2] = value;
      out[o + 3] = pixels[o + 3]!;
    }
  }
  return out;
}

/**
 * 查找边缘：没有参数、没有对话框（跟真实 Photoshop 一致，点了菜单直接
 * 应用）。逐通道 Sobel 梯度幅值，反相后输出——边缘在白底上显示为暗线，
 * 跟 PS「查找边缘」的视觉效果一致。
 */
export async function findEdges(
  pixels: Uint8Array,
  width: number,
  height: number,
  options: FilterOptions = {},
): Promise<Uint8Array> {
  throwIfAborted(options.signal);
  const out = new Uint8Array(pixels.length);
  const at = (x: number, y: number, c: number): number => sampleClamped(pixels, width, height, x, y, c);
  for (let y = 0; y < height; y++) {
    if (y % 64 === 0) throwIfAborted(options.signal);
    for (let x = 0; x < width; x++) {
      const o = (y * width + x) * 4;
      for (let c = 0; c < 3; c++) {
        const gx =
          at(x - 1, y - 1, c) + 2 * at(x - 1, y, c) + at(x - 1, y + 1, c) -
          (at(x + 1, y - 1, c) + 2 * at(x + 1, y, c) + at(x + 1, y + 1, c));
        const gy =
          at(x - 1, y - 1, c) + 2 * at(x, y - 1, c) + at(x + 1, y - 1, c) -
          (at(x - 1, y + 1, c) + 2 * at(x, y + 1, c) + at(x + 1, y + 1, c));
        const magnitude = Math.sqrt(gx * gx + gy * gy) / 4; // 归一化到大致 0–255 量级
        out[o + c] = Math.round(Math.min(255, Math.max(0, 255 - magnitude)));
      }
      out[o + 3] = pixels[o + 3]!;
    }
  }
  return out;
}
